import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { useAppDispatch, useAppSelector } from '@/app/hook';
import { PSButton } from '@/components/ui/button';
import { selectUseId } from '@/features/auth/auth.slice';
import { resetUndoRedoScenario } from '@/features/scenario.slice';
import { BlockType } from '@/interfaces/models/Knowledge/scenario.interface';

import { createRandomString, isAlphabetic } from '../../../../tools';
import AddNoteButton from '../../AddNoteButton';
import NodeParentChanger from '../../NodeParentChanger';
import NoteForm, { getNoteSchema, INoteForm } from '../../NoteForm';
import ScenarioHeader from '../../ScenarioHeader';
import { MacBotActionKind } from '../../types';
import { MacBotButtonSettingForm } from './scenario-button-setting-form.component';
import TextScenarioDeleteButton from './scenario-delete-button';
import ScenarioSide from './scenario-side';
import { TextInputWithHint } from './TextInputWithHint';

export const MacBotButtonForm = ({
  setShow,
  itemData,
  parentId,
  tree,
  loading,
  createScenarioBlock,
  deleteScenarioBlock,
  changeScenarioBlock,
}: {
  setShow?: (item: boolean) => void;
  itemData: any;
  parentId: string | null;
  tree: any[];
  loading: boolean;
  createScenarioBlock: (block: any) => Promise<void>;
  deleteScenarioBlock: (block: any, isUpdateTree: boolean, isUndoRedoState: boolean) => Promise<void>;
  changeScenarioBlock: (block: any, isUpdateTree: boolean, isUndoRedoState: boolean) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const [showButtonSetting, setShowButtonSetting] = useState(false);
  const [selectedButtonToEdit, setSelectedButtonToEdit] = useState<number>(-1);
  const [quickReplies, setQuickReplies] = useState<any[]>([]);
  const dispatch: any = useAppDispatch();
  const userId = useAppSelector(selectUseId);

  interface IResponseForm extends INoteForm {
    BlockTitle?: string;
    BotResponse: string;
    OrderId?: number;
  }

  const schema = Yup.object({
    BotResponse: Yup.string().required(t('MacBotButtonForm/l-04') + ' ' + t('MacBotMediaForm/scenario_variable_name_required')),
    OrderId: Yup.number().optional(),
    ...getNoteSchema(t),
  });

  const {
    register,
    formState: { errors },
    reset,
    setValue,
    handleSubmit,
    getValues,
    watch,
    control,
  } = useForm<IResponseForm>({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    defaultValues: {
      notes: [
        {
          note_id: uuidv4(),
          creation_date_time: new Date().toISOString(),
          title: '',
          description: '',
          qc: false,
          author: userId,
        },
      ],
    },
  });

  const { fields } = useFieldArray({
    control,
    name: 'notes',
  });
  const [isDelete, setIsDelete] = useState(true);

  const deleteHandler = () => {
    deleteScenarioBlock(itemData, true, true);
  };

  const getOrderIndexId = (itemData: any) => {
    return getOrderButtonsList().findIndex(dr => dr.order_id == itemData.order_id && dr.id == itemData.id) + 1;
  };

  const removeButtonHandler = (btnIndex: number) => {
    setQuickReplies(prev => prev.filter((p, index) => index !== btnIndex));
  };

  const addButtonHandler = (qBtn: number) => {
    setQuickReplies(prev => [...prev, qBtn]);
  };

  const editButtonHandler = (_index: number, qRep: any) => {
    setQuickReplies(prev => prev.map((p, index) => (index === _index ? qRep : p)));

    setSelectedButtonToEdit(-1);
  };

  const handleQuickReplySelect = (index: number) => {
    setSelectedButtonToEdit(index);
    setShowButtonSetting(true);
  };

  const closeButtonSettingModal = () => {
    setShowButtonSetting(false);
    setSelectedButtonToEdit(-1);
  };

  const createTitle = (text: string) => {
    let titleCode = 0;
    tree.forEach(element => {
      if (element.type === BlockType.Response) {
        const _code = element.title.split('-');
        if (_code[0] && Number(_code[0]) > titleCode) titleCode = +_code[0];
      }
    });

    if (typeof text != 'string' || !text) {
      return '';
    }
    let acronym = text
      ?.match(/[\p{Alpha}\p{Nd}]+/gu)
      ?.reduce((previous, next) => previous + (+next === 0 || parseInt(next) ? parseInt(next) : next[0] || ''), '')
      ?.toUpperCase();

    if (!acronym || (acronym && acronym.length < 3) || !isAlphabetic(acronym)) {
      acronym = createRandomString(5);
    }

    return `${titleCode + 1}-${acronym?.slice(0, 3)}`;
  };

  function delay(milliseconds: number) {
    return new Promise(resolve => setTimeout(resolve, milliseconds));
  }

  const ButtonSubmit = (data: any) => {
    let orderId = null;

    if (data.OrderId && data.OrderId !== '') {
      const orderButtonsList = getOrderButtonsList();
      orderId = orderButtonsList[data.OrderId - 1].order_id;
    }

    let _data = {
      type: BlockType.Button,
      title: data.BotResponse,
      label: data.BotResponse,
      parent: itemData ? itemData.parent : parentId,
      order_id: itemData ? +orderId : getNewOrderButtons(),
      notes: data.notes,
    };

    if (itemData) {
      if (orderId !== itemData.order_id) {
        const replaceNode = tree.find(dr => dr.parent === itemData.parent && dr.order_id === orderId);

        if (replaceNode) {
          dispatch(resetUndoRedoScenario());
          const updatedNode = { ...replaceNode, order_id: itemData.order_id };
          changeScenarioBlock(updatedNode, false, false).then(() => {
            delay(500).then(() => {
              _data = { ...itemData, ..._data, id: itemData.id };
              changeScenarioBlock(_data, true, false);
            });
          });
        }
      } else {
        delay(500).then(res => {
          _data = { ...itemData, ..._data, id: itemData.id };
          changeScenarioBlock(_data, true, false);
        });
      }
    } else {
      createScenarioBlock(_data);
    }
  };

  const getOrderButtonsList = (): any[] => {
    const parent = itemData ? itemData.parent : parentId;
    const filteredTree = tree.filter(dr => dr.parent == parent && (dr.type === BlockType.Button || dr.type === BlockType.TextLink));
    const sortedTree = filteredTree.sort((a, b) => a.order_id - b.order_id);
    return sortedTree;
  };

  const getNewOrderButtons = () => {
    const orderButtonsList = getOrderButtonsList();
    const node = orderButtonsList[orderButtonsList.length - 1];

    if (!node) return 1;

    for (let i = 1; i < 20; i++) {
      if (orderButtonsList.find(dr => dr.order_id != node.order_id + i)) {
        return node.order_id + i;
      }
    }
  };

  const onOrderChange = (event: any) => {
    setValue('OrderId', +event.target.value);
  };

  useEffect(() => {
    if (!itemData) {
      setIsDelete(false);
    } else {
      reset({
        BotResponse: itemData?.label ?? '',
        OrderId: getOrderIndexId(itemData) ?? 0,
        notes: itemData?.notes,
      });
    }
  }, [itemData]);

  return (
    <React.Fragment>
      <ScenarioSide size="md" onSubmit={handleSubmit(ButtonSubmit)}>
        <ScenarioHeader
          type={MacBotActionKind.ShowButtonForm}
          onClose={() => {
            reset();
          }}
        />
        <div className="tw-p-3 tw-bg-gray-50 tw-flex tw-flex-col tw-gap-y-4 tw-w-full">
          <TextInputWithHint
            label={t('MacBotButtonForm/l-04')}
            name="BotResponse"
            register={register('BotResponse')}
            error={errors.BotResponse}
            max={20}
            maxLength={20}
            HintMessage={['MacBotButtonForm/maxlenght']}
          />

          {itemData && (
            <Form.Group className="tw-w-max">
              <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">{t('MacBotButtonForm/l-06')}</Form.Label>
              <Form.Select
                translate="no"
                className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                aria-label={t('MacBotGotoForm/l-04')}
                {...register('OrderId')}
              >
                {getOrderButtonsList()?.map((dr, index) => (
                  <option key={dr.id}>{index + 1}</option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
        </div>

        <div className="tw-flex flex-row tw-justify-between tw-items-center">
          <div className="tw-h-11 tw-justify-start tw-items-center tw-gap-2 tw-inline-flex">
            <TextScenarioDeleteButton itemData={itemData} tree={tree} deleteHandler={deleteHandler} />

            {itemData?.id && <NodeParentChanger tree={tree} currentId={itemData.id} />}
            <AddNoteButton noteFormMethods={{ getValues, setValue }} />
          </div>
          <div className="tw-flex tw-justify-end">
            <PSButton id="btn-submit-scenario" type="submit" disabled={loading} isLoading={loading} title={t('MacBotResponseForm/btn-02')}></PSButton>
          </div>
        </div>
        <NoteForm noteFormMethods={{ register, watch, errors, fields, setValue }} />
      </ScenarioSide>

      <MacBotButtonSettingForm
        show={showButtonSetting}
        closeModal={closeButtonSettingModal}
        addQuickReplies={addButtonHandler}
        editQuickReplies={editButtonHandler}
        selectedButtonToEdit={selectedButtonToEdit}
        quickReplies={quickReplies}
        removeButton={removeButtonHandler}
        tree={tree}
      />
    </React.Fragment>
  );
};
